import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  FileText,
  Shield,
  Video,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { consentService, ConsentTemplate } from '@/services/consentService';
import ConsentSignature, { SignatureData } from '@/components/consent/ConsentSignature';

const FORM_TYPE_ICONS: Record<string, React.ReactNode> = {
  telehealth: <Video className="w-5 h-5" />,
  hipaa: <Shield className="w-5 h-5" />,
  financial: <FileText className="w-5 h-5" />,
  treatment: <FileText className="w-5 h-5" />,
  custom: <FileText className="w-5 h-5" />,
};

const FORM_TYPE_LABELS: Record<string, string> = {
  telehealth: 'Telehealth Services Consent',
  hipaa: 'HIPAA Privacy Authorization',
  financial: 'Financial Responsibility Agreement',
  treatment: 'Treatment Consent',
  custom: 'Consent Form',
};

interface PatientConsentSigningPageProps {
  // Optional props for embedded use
  templateId?: string;
  patientId?: string;
  clinicId?: string;
  patientName?: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const PatientConsentSigningPage: React.FC<PatientConsentSigningPageProps> = ({
  templateId: propTemplateId,
  patientId: propPatientId,
  clinicId: propClinicId,
  patientName,
  onComplete,
  onCancel,
}) => {
  const { templateId: paramTemplateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Use props or URL params
  const templateId = propTemplateId || paramTemplateId;
  const patientId = propPatientId || searchParams.get('patientId');
  const clinicId = propClinicId || searchParams.get('clinicId');
  const returnUrl = searchParams.get('returnUrl');

  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasReadConsent, setHasReadConsent] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    } else {
      setError('No consent template specified');
      setLoading(false);
    }
  }, [templateId]);

  const fetchTemplate = async () => {
    if (!templateId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await consentService.getPublicConsentTemplate(templateId);

      if (response.success && response.data) {
        setTemplate(response.data);
      } else {
        setError('Consent form not found or is no longer available');
      }
    } catch (err: any) {
      console.error('Failed to fetch consent template:', err);
      setError(err.message || 'Failed to load consent form');
    } finally {
      setLoading(false);
    }
  };

  const handleSignature = async (signatureData: SignatureData) => {
    if (!template || !patientId || !clinicId) {
      toast({
        title: 'Error',
        description: 'Missing required information to submit consent',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      const response = await consentService.submitPublicConsentSignature({
        patient_id: patientId,
        clinic_id: clinicId,
        consent_form_template_id: template.id,
        signature_type: signatureData.type,
        signature_data: signatureData,
      });

      if (response.success) {
        setIsComplete(true);
        toast({
          title: 'Consent Signed',
          description: 'Your consent has been recorded successfully',
        });

        // Call onComplete callback if provided
        if (onComplete) {
          setTimeout(onComplete, 1500);
        } else if (returnUrl) {
          setTimeout(() => navigate(returnUrl), 1500);
        }
      } else {
        toast({
          title: 'Error',
          description: 'Failed to submit consent signature',
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      console.error('Failed to submit consent:', err);
      toast({
        title: 'Error',
        description: err.message || 'Failed to submit consent signature',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate(-1);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading consent form...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">Unable to Load Consent</h3>
            <p className="text-muted-foreground text-center mb-4">
              {error || 'The consent form could not be loaded'}
            </p>
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (isComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-medium mb-2">Consent Signed Successfully</h3>
            <p className="text-muted-foreground text-center mb-4">
              Thank you for signing the {template.name}. Your consent has been recorded.
            </p>
            {!onComplete && (
              <Button onClick={handleBack}>Continue</Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const typeIcon = FORM_TYPE_ICONS[template.form_type] || FORM_TYPE_ICONS.custom;
  const typeLabel = FORM_TYPE_LABELS[template.form_type] || FORM_TYPE_LABELS.custom;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={handleBack} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              {typeIcon}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                {template.clinic?.name || 'NovaMed'}
              </p>
              <p className="text-sm font-medium text-primary">
                {template.clinic?.name ? 'Partnered with NovaMed' : 'Powered by NovaMed'}
              </p>
              <p className="text-sm text-muted-foreground">{typeLabel}</p>
              <h1 className="text-2xl font-bold">{template.name}</h1>
              {template.description && (
                <p className="text-muted-foreground mt-1">{template.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Missing info warning */}
        {(!patientId || !clinicId) && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Missing patient or clinic information. Please access this form through the proper link.
            </AlertDescription>
          </Alert>
        )}

        {/* Consent Content */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Please Read Carefully</CardTitle>
            <CardDescription>
              Review the following consent form in its entirety before signing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="prose prose-sm max-w-none dark:prose-invert
                [&>h2]:text-lg [&>h2]:font-semibold [&>h2]:mt-4 [&>h2]:mb-2
                [&>h3]:text-base [&>h3]:font-medium [&>h3]:mt-3 [&>h3]:mb-2
                [&>p]:text-sm [&>p]:mb-3 [&>p]:leading-relaxed
                [&>ul]:text-sm [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-6
                [&>ol]:text-sm [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6
                [&>li]:mb-1"
              dangerouslySetInnerHTML={{ __html: template.content }}
            />
          </CardContent>
        </Card>

        {/* Acknowledgement */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="read-consent"
                checked={hasReadConsent}
                onCheckedChange={(checked) => setHasReadConsent(checked === true)}
                disabled={submitting}
              />
              <Label
                htmlFor="read-consent"
                className="text-sm leading-relaxed cursor-pointer"
              >
                I have read and understand the above consent form. I acknowledge that by signing
                below, I am providing my informed consent and agree to the terms described above.
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        {template.signature_required && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Signature</CardTitle>
              <CardDescription>
                Please provide your signature below to complete this consent form.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConsentSignature
                onSign={handleSignature}
                disabled={submitting || !patientId || !clinicId}
                patientName={patientName}
                isconsentread={hasReadConsent}
              />

              {submitting && (
                <div className="flex items-center justify-center mt-4 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Submitting your consent...
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Non-signature consent (checkbox only) */}
        {!template.signature_required && (
          <Card>
            <CardContent className="pt-6">
              <Button
                onClick={() =>
                  handleSignature({
                    type: 'typed',
                    data: 'Acknowledged',
                    timestamp: new Date().toISOString(),
                  })
                }
                disabled={!hasReadConsent || submitting || !patientId || !clinicId}
                className="w-full disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    I Agree to the Terms Above
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Footer info */}
        <div className="mt-6 text-center text-xs text-muted-foreground">
          <p>Your signature will be securely recorded with timestamp and IP address for verification.</p>
          <p className="mt-1">Date: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default PatientConsentSigningPage;


